import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ScanLine, Globe, TrendingUp, ArrowRight, Check, Brain, Shield, Languages,
  Receipt, CreditCard, Crown, Loader2, Zap, Sparkles, Facebook, Instagram, Youtube, Quote
} from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import MoneyTree from "@/components/MoneyTree";
import PlanCard from "@/components/PlanCard";
import { StaggerGroup, StaggerItem } from "@/components/motion/Stagger";
import { trackLandingViewed, trackPlanSelected, trackCheckoutStarted } from "@/lib/tracking";

export default function LandingPage() {
  const { t, lang, setLang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [billing, setBilling] = useState("monthly"); // monthly | yearly
  const [showLifetime, setShowLifetime] = useState(false); // lifetime demoted to a secondary reveal
  const [loadingPkg, setLoadingPkg] = useState(null);
  const [usdtPrice, setUsdtPrice] = useState(null);

  // Live USDT price
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fx/binance-p2p?asset=USDT&fiat=DOP&trade_type=SELL`,
      { headers: { "X-Anon": "1" } })
      .then(r => r.ok ? r.json() : null)
      .then(d => d?.price && setUsdtPrice(d.price))
      .catch(() => {});
  }, []);

  // Funnel event 1 — fires once per landing view, carries the ?plan= if the
  // visitor arrived from an ad already pointing at a specific plan.
  useEffect(() => {
    const plan = new URLSearchParams(window.location.search).get("plan");
    trackLandingViewed({ plan });
  }, []);

  const handleCheckout = async (packageId) => {
    if (!user) {
      // Not signed in yet: this click is "I want this plan", not a real
      // checkout start (that happens post-registration in ActivatePage).
      trackPlanSelected(packageId, billing);
      nav(`/register?plan=${packageId}`);
      return;
    }
    setLoadingPkg(packageId);
    try {
      const r = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        trackCheckoutStarted(packageId, r.data.session_id || packageId);
        window.location.href = r.data.url;
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Error iniciando el pago");
    } finally {
      setLoadingPkg(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0a10] text-white overflow-x-hidden">
      {/* Scarcity bar */}
      <div className="bg-gradient-to-r from-violet-500 via-fuchsia-500 to-violet-500 text-white text-center py-2 text-xs sm:text-sm font-medium" data-testid="scarcity-bar">
        Lanzamiento · acceso de por vida por $100 USD (60% off) — oferta por tiempo limitado
      </div>

      {/* HEADER */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0b0a10]/85 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img src="/logo.png" alt="The Trading Dock" className="w-11 h-11 rounded-xl ring-1 ring-white/10" />
            <div className="leading-tight">
              <div className="text-base font-black tracking-tight text-white">THE TRADING DOCK<sup className="text-[8px] opacity-60">®</sup></div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-bold">finance</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-300">
            <a href="#features" className="hover:text-violet-300 transition">Funciones</a>
            <a href="#pricing" className="hover:text-violet-300 transition">Precios</a>
            <a href="#faq" className="hover:text-violet-300 transition">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={() => setLang(lang === "es" ? "en" : "es")} data-testid="lang-toggle" className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white border border-white/10 hover:bg-white/5 px-2.5 py-1.5 rounded-lg font-bold uppercase">
              <Languages className="w-3.5 h-3.5" /> {lang === "es" ? "EN" : "ES"}
            </button>
            <Link to="/login" data-testid="header-login" className="text-sm text-slate-300 hover:text-white px-3 py-2">Entrar</Link>
            <button onClick={() => handleCheckout("pro_special")} data-testid="header-cta" className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white text-sm font-bold px-4 py-2 rounded-xl shadow-[0_8px_30px_-10px_rgba(167,139,250,0.6)]">
              Probar por $1
            </button>
          </div>
        </div>
      </header>

      {/* HERO — split layout: copy + money tree visual */}
      <section className="relative px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 bg-fintech-blocks bg-blocks-overlay overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center">
          {/* Copy column */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.08] border border-white/15 text-xs text-white/90 mb-6 backdrop-blur-sm">
              <Sparkles className="w-3.5 h-3.5" /> Asistente con IA · OCR · Binance P2P en vivo
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.0]" data-testid="hero-title">
              El dinero que <span className="text-white drop-shadow-[0_0_20px_rgba(255,255,255,0.5)]">no ves,</span><br />
              se te escapa.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/80 max-w-xl leading-relaxed mx-auto lg:mx-0">
              Un panel financiero personal con multi-divisa, OCR de vouchers y conciliación bancaria.
              Pensado para quien gana en USD, gasta en pesos y aún no quiere depender de Excel.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3" data-testid="hero-cta-row">
              <button onClick={() => handleCheckout("pro_special")} data-testid="hero-cta-primary"
                className="bg-white hover:bg-slate-100 text-violet-900 font-bold px-7 py-4 rounded-2xl text-sm shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)] inline-flex items-center gap-2 transition-all">
                Probar 7 días por $1 <ArrowRight className="w-4 h-4" />
              </button>
              <a href="#pricing" className="text-sm text-white/90 hover:text-white px-6 py-4 rounded-2xl border border-white/25 hover:bg-white/10 backdrop-blur-sm">
                Ver acceso de por vida →
              </a>
            </div>
            <div className="mt-5 text-xs text-white/60">Pagos por Stripe · Cancela antes del día 7 y no se te cobra nada más</div>

            {usdtPrice && (
              <div className="mt-10 inline-flex items-center gap-3 bg-black/30 border border-white/20 rounded-full px-5 py-2.5 text-xs backdrop-blur-md">
                <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" /></span>
                <span className="text-white/80">USDT/DOP en vivo ·</span>
                <span className="font-bold tabular-nums text-emerald-300">RD$ {Number(usdtPrice).toFixed(2)}</span>
                <span className="text-white/60">· Binance P2P</span>
              </div>
            )}
          </div>

          {/* Money Tree visual */}
          <div className="relative flex items-center justify-center" data-testid="hero-money-tree">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(196,181,253,0.25),transparent_60%)] blur-2xl" />
            <MoneyTree className="relative w-full max-w-[420px] text-white/85 drop-shadow-[0_10px_40px_rgba(167,139,250,0.4)]" accent="#c4b5fd" />
            {/* Caption underneath */}
            <div className="absolute bottom-[-8px] left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.32em] text-white/60 font-bold whitespace-nowrap">
              tu árbol financiero
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES — 3 succinct cards */}
      <section id="features" className="relative px-5 sm:px-8 py-16 bg-gradient-to-b from-violet-500/[0.06] via-transparent to-transparent overflow-hidden">
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.28em] text-violet-300 font-bold">QUÉ HACE</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">Reemplaza 5 apps, Excel y tu memoria.</h2>
          </div>
          <StaggerGroup className="grid sm:grid-cols-3 gap-5">
            {[
              { icon: ScanLine, title: "OCR con IA", desc: "Foto del voucher → GPT-5 extrae monto, fecha y comercio en 3 segundos." },
              { icon: Globe, title: "Multi-divisa en vivo", desc: "DOP, USD, EUR, USDT + 6 más. Conversión real al momento sin sumar mentalmente." },
              { icon: Brain, title: "Asistente del día", desc: "“Multi Card cierra hoy. USDT +0.4%. Te queda 11% del presupuesto.”" },
              { icon: CreditCard, title: "Tarjetas inteligentes", desc: "Bancos, corte, pago y sobregiro. Alertas 3 días antes del vencimiento." },
              { icon: Receipt, title: "Deudas con 4 formas de abono", desc: "Cuenta, débito, crédito o efectivo. Balances ajustados automáticamente." },
              { icon: TrendingUp, title: "Conciliación bancaria", desc: "Sube el CSV del banco. Cruzamos e importamos lo que falta sin duplicar." },
            ].map((f) => (
              <StaggerItem key={f.title}>
                <FeatureCard icon={f.icon} title={f.title} desc={f.desc} />
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* PRICING — minimal, direct, with lifetime offer */}
      <section id="pricing" className="relative px-5 sm:px-8 py-20 bg-fintech-blocks-light bg-blocks-overlay overflow-hidden">
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.28em] text-violet-300 font-bold">PRECIOS</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">Elige tu plan.</h2>
            <p className="mt-3 text-sm text-slate-400">Sin trucos. Sin letra pequeña. Cancela cuando quieras.</p>
          </div>

          {/* Billing toggle — primary conversion goal is the $1 trial, so the toggle
              only carries Mensual/Anual. Lifetime is demoted to a secondary reveal below. */}
          <div className="flex justify-center mb-10" data-testid="pricing-toggle">
            <div className="inline-flex bg-white/[0.04] border border-white/10 rounded-2xl p-1">
              <BillToggle active={billing === "monthly"} onClick={() => setBilling("monthly")} testid="bill-monthly">Mensual</BillToggle>
              <BillToggle active={billing === "yearly"} onClick={() => setBilling("yearly")} testid="bill-yearly">
                Anual <span className="ml-1.5 text-[9px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded font-bold">-20%</span>
              </BillToggle>
            </div>
          </div>

          {/* PLANS GRID */}
          <StaggerGroup className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-testid="plans-grid">
            <StaggerItem className="h-full">
              <PlanCard
                variant="landing"
                name="Pro"
                desc="Para tu economía personal."
                amount={billing === "monthly" ? 5 : 48}
                period={billing === "monthly" ? "/mes" : "/año"}
                accent="violet"
                features={["Cuentas ilimitadas", "Transacciones ilimitadas", "OCR ilimitado con IA", "Multi-divisa en vivo + USDT P2P", "Tarjetas + deudas + recurrentes", "Conciliación CSV de bancos"]}
                onClick={() => handleCheckout(billing === "monthly" ? "pro_special" : "pro_yearly")}
                loading={loadingPkg === (billing === "monthly" ? "pro_special" : "pro_yearly")}
                ctaText={billing === "monthly" ? "Probar 7 días por $1" : "Suscribirme · ahorra $12/año"}
                testid="plan-pro"
                special={billing === "monthly"}
              />
            </StaggerItem>
            <StaggerItem className="h-full">
              <PlanCard
                variant="landing"
                name="Familia"
                desc="Tú, tu pareja y gastos compartidos."
                amount={billing === "monthly" ? 9 : 84}
                period={billing === "monthly" ? "/mes" : "/año"}
                accent="fuchsia"
                badge="Mejor valor"
                features={["Todo lo del Pro", "Hasta 4 usuarios", "Presupuestos compartidos", "Multi-divisa por miembro", "Reportes consolidados", "Soporte prioritario"]}
                onClick={() => handleCheckout(billing === "monthly" ? "family_special" : "family_yearly")}
                loading={loadingPkg === (billing === "monthly" ? "family_special" : "family_yearly")}
                ctaText={billing === "monthly" ? "Probar 7 días por $1" : "Suscribirme · ahorra $24/año"}
                testid="plan-family"
                special={billing === "monthly"}
              />
            </StaggerItem>
          </StaggerGroup>

          <p className="mt-8 text-center text-xs text-slate-500">
            {billing === "monthly" && "Día 0: $1. Día 7: $4 (Pro) o $8 (Familia) si no cancelas. Mes 2+: $5 o $9."}
            {billing === "yearly" && "Cobro único anual · acceso completo · cancela cuando quieras."}
          </p>

          {/* Lifetime — secondary offer, revealed on demand so it never competes
              visually with the primary $1 trial goal. Same lifetime_offer SKU. */}
          <div className="mt-10 max-w-2xl mx-auto" data-testid="lifetime-reveal">
            {!showLifetime ? (
              <div className="text-center">
                <button
                  onClick={() => setShowLifetime(true)}
                  data-testid="lifetime-reveal-toggle"
                  className="text-sm text-slate-400 hover:text-violet-200 font-semibold transition-colors inline-flex items-center gap-1.5"
                >
                  ¿Prefieres un pago único? Ver acceso de por vida
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <LifetimeCard
                onClick={() => handleCheckout("lifetime_offer")}
                loading={loadingPkg === "lifetime_offer"}
              />
            )}
          </div>
        </div>
      </section>

      {/* CASOS DE ÉXITO — testimonios reales con cifras concretas */}
      <section id="cases" className="relative px-5 sm:px-8 py-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-0 w-[400px] h-[400px] rounded-full bg-violet-500/10 blur-[140px]" />
          <div className="absolute top-1/2 right-0 w-[400px] h-[400px] rounded-full bg-fuchsia-500/10 blur-[140px]" />
        </div>
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.28em] text-violet-300 font-bold">CASOS DE ÉXITO</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">Lo que cambió cuando lo usaron.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <CaseCard
              avatar="C"
              avatarColor="from-violet-500 to-fuchsia-500"
              name="Carla M."
              role="Diseñadora freelance · RD"
              stat="ahorró $1,840 al año"
              quote="Cobro en USD y gasto en pesos. Antes vivía con la calculadora abierta. Ahora veo mi patrimonio real en tiempo real y dejé de cambiar dólares al ojo."
              highlight={["$1,840", "patrimonio real"]}
            />
            <CaseCard
              avatar="J"
              avatarColor="from-amber-500 to-violet-500"
              name="Jorge R."
              role="Padre + emprendedor · DR/US"
              stat="0 pagos atrasados en 6 meses"
              quote="Tenía 4 tarjetas y siempre olvidaba alguna. Las alertas 3 días antes del corte me cambiaron la vida. La mora se acabó."
              highlight={["0 pagos atrasados", "alertas 3 días"]}
            />
            <CaseCard
              avatar="L"
              avatarColor="from-fuchsia-500 to-rose-500"
              name="Laura V."
              role="Trader cripto · LATAM"
              stat="30 min/día recuperados"
              quote="USDT/DOP en vivo y comisiones LBTR automáticas. Lo que me tomaba media hora en Excel ahora es un escaneo de voucher de 3 segundos."
              highlight={["30 min/día", "3 segundos"]}
            />
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-slate-400">
            <span className="flex items-center gap-2"><Shield className="w-3.5 h-3.5 text-violet-300" /> Pagos seguros con Stripe</span>
            <span className="flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5 text-emerald-300" /> RD · US · MX · CO · AR</span>
          </div>
        </div>
      </section>

      {/* FAQ — elaborated */}
      <section id="faq" className="relative px-5 sm:px-8 py-16">
        <div className="relative max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.28em] text-violet-300 font-bold">FAQ</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">Todo lo que necesitas saber.</h2>
            <p className="mt-3 text-sm text-slate-400">Si tu pregunta no está aquí, escríbenos a <a href="mailto:soporte@thetradingdock.com" className="text-violet-300 hover:text-violet-200 font-semibold">soporte@thetradingdock.com</a></p>
          </div>
          <div className="space-y-3">
            <Faq
              q="¿Cómo funciona exactamente la oferta de $1 por 7 días?"
              a="Pagas $1 hoy y tienes acceso completo durante 7 días con todas las funciones desbloqueadas. Si el día 7 no cancelas, cobramos $4 (plan Pro) o $8 (plan Familia) completando el primer mes. A partir del mes 2 en adelante el cobro es de $5/mes (Pro) o $9/mes (Familia). Puedes cancelar desde tu Perfil con un click — sin penalización ni preguntas."
            />
            <Faq
              q="¿Qué incluye exactamente el acceso de por vida (Lifetime)?"
              a="Un pago único de $100 USD (oferta de lanzamiento; precio regular $250). Te da acceso completo al plan Pro a perpetuidad: cuentas ilimitadas, transacciones ilimitadas, OCR ilimitado con IA, multi-divisa en vivo, todas las funciones futuras gratis y soporte prioritario. NUNCA se renueva — pagas una vez y se acabó. Es transferible 1 vez (por si decides pasarlo a tu pareja o familiar)."
            />
            <Faq
              q="¿Mi información financiera está segura? ¿Quién puede verla?"
              a="Solamente tú. Tu cuenta es un espacio aislado: tus transacciones, deudas y tarjetas viven en un namespace propio que ningún otro usuario puede ver — ni siquiera nosotros, salvo que nos autorices explícitamente para soporte. Las contraseñas se guardan con bcrypt (irreversible), la sesión usa JWT cifrado y todo viaja por HTTPS. Los pagos pasan por Stripe; nunca tocamos tu tarjeta directamente."
            />
            <Faq
              q="¿Necesito conectar mi banco con credenciales?"
              a="No. The Trading Dock NO se conecta a tu banco con login. Tú decides cómo cargar los movimientos: manualmente, escaneando vouchers con la cámara (OCR con IA) o subiendo el CSV mensual que descargas de la app de tu banco. Esto significa cero riesgo de exposición de credenciales bancarias."
            />
            <Faq
              q="¿Cómo funciona el OCR con IA?"
              a="Tomas foto de cualquier voucher, factura o estado bancario y nuestra IA (GPT-5.2) extrae automáticamente el monto, fecha, comercio, moneda y categoría sugerida. Tardas 3 segundos en registrar lo que en Excel te tomaría 1 minuto. Funciona con vouchers de bancos dominicanos, app de Banreservas, Popular, BHD, Caribe Express, Multi Card y más."
            />
            <Faq
              q="¿Funciona con USDT, Bitcoin y cripto?"
              a="Sí. USDT↔DOP usa la mediana de Binance P2P actualizada en tiempo real (no hay que adivinar la tasa). Puedes registrar saldos en BTC, ETH, USDT, USDC y otras stablecoins. Los precios de fiat (DOP, USD, EUR, MXN, COP, ARS, BRL, etc.) se actualizan desde open.er-api.com cada vez que se carga el dashboard."
            />
            <Faq
              q="¿Puedo usarla con mi pareja o familia?"
              a="El plan Familia ($9/mes o $84/año) permite hasta 4 miembros bajo una sola cuenta. Cada uno tiene su perfil pero pueden compartir presupuestos, ver consolidados y manejar gastos comunes (renta, supermercado, escuela). Cada miembro puede operar en su moneda preferida — útil si uno gana USD y otro DOP."
            />
            <Faq
              q="¿Funciona en mi teléfono?"
              a="Totalmente. Es una PWA (Progressive Web App): en iPhone Safari haces Compartir → 'Añadir a inicio' y queda como app nativa. En Android Chrome, el menú ⋮ → 'Instalar app'. Necesitas conexión a internet para usarla — no guarda datos para consultarlos sin conexión."
            />
            <Faq
              q="¿Cómo cancelo mi suscripción?"
              a="Desde tu Perfil → sección Suscripción → botón 'Cancelar'. Mantienes acceso hasta el final del período pagado y no se renueva. Cero burocracia, sin llamadas, sin retención agresiva. Si cancelas antes del día 7 del trial, evitas por completo el cobro de $4/$8 — no emitimos reembolsos prorrateados por períodos ya facturados, salvo error nuestro."
            />
            <Faq
              q="¿Qué pasa con mis datos si cancelo?"
              a="Tus datos siguen accesibles en modo lectura durante 60 días después de cancelar (por si decides volver). Después de los 60 días puedes solicitar exportarlos en CSV o JSON, y al día 90 se eliminan permanentemente de nuestros servidores. Cumplimos GDPR y la Ley 172-13 de RD."
            />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative px-5 sm:px-8 py-20 bg-fintech-blocks bg-blocks-overlay overflow-hidden">
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight text-white">Tu dinero se mueve <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-fuchsia-300 to-cyan-300">con o sin ti.</span></h2>
          <p className="mt-4 text-base text-white/85">Empieza por $1 hoy o asegura acceso de por vida por $100.</p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={() => handleCheckout("pro_special")} data-testid="final-cta-trial" className="bg-white hover:bg-slate-100 text-violet-900 font-bold px-7 py-4 rounded-2xl text-sm shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)] inline-flex items-center gap-2 transition-all">
              Probar por $1 <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => handleCheckout("lifetime_offer")} data-testid="final-cta-lifetime" className="bg-black/30 hover:bg-black/40 border border-amber-300/40 text-amber-200 hover:text-amber-100 font-bold px-7 py-4 rounded-2xl text-sm inline-flex items-center gap-2 backdrop-blur-md">
              <Crown className="w-4 h-4" /> Lifetime · $100 USD
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 bg-[#08070d]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            {/* Brand col */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo.png" alt="The Trading Dock" className="w-10 h-10 rounded-xl ring-1 ring-white/10" />
                <div>
                  <div className="text-sm font-black tracking-tight">THE TRADING DOCK<sup className="text-[8px] opacity-60 ml-0.5">®</sup></div>
                  <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-bold">finance · since 2026</div>
                </div>
              </div>
              <p className="text-sm text-slate-400 max-w-sm leading-relaxed">
                Tu panel financiero personal con multi-divisa, OCR e IA. Construido para quien quiere ver, no adivinar.
              </p>
              {/* SOCIAL ICONS — wired to placeholder hrefs; founder will replace later */}
              <div className="mt-5 flex items-center gap-3" data-testid="footer-socials">
                <SocialBtn href="https://www.facebook.com/thetradingdock" label="Facebook" testid="social-fb">
                  <Facebook className="w-4 h-4" />
                </SocialBtn>
                <SocialBtn href="https://www.instagram.com/thetradingdock" label="Instagram" testid="social-ig">
                  <Instagram className="w-4 h-4" />
                </SocialBtn>
                <SocialBtn href="https://www.tiktok.com/@thetradingdock" label="TikTok" testid="social-tt">
                  {/* TikTok icon (lucide doesn't ship one — inline SVG) */}
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.18a8.35 8.35 0 0 0 4.85 1.55v-3.4a4.85 4.85 0 0 1-1.92-.64z" />
                  </svg>
                </SocialBtn>
                <SocialBtn href="https://www.youtube.com/@thetradingdock" label="YouTube" testid="social-yt">
                  <Youtube className="w-4 h-4" />
                </SocialBtn>
              </div>
              <p className="mt-3 text-[11px] text-slate-600">
                Síguenos para tips de finanzas personales, multi-divisa y cripto.
              </p>
            </div>

            {/* Producto */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">Producto</div>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#features" className="hover:text-white transition">Funciones</a></li>
                <li><a href="#cases" className="hover:text-white transition">Casos de éxito</a></li>
                <li><a href="#pricing" className="hover:text-white transition">Precios</a></li>
                <li><a href="#faq" className="hover:text-white transition">FAQ</a></li>
                <li><Link to="/login" className="hover:text-white transition">Entrar</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">Legal & contacto</div>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/privacidad" className="hover:text-white transition" data-testid="footer-privacy">Política de privacidad</Link></li>
                <li><Link to="/terminos" className="hover:text-white transition" data-testid="footer-terms">Términos de servicio</Link></li>
                <li><Link to="/terminos#reembolsos" className="hover:text-white transition" data-testid="footer-refunds">Política de reembolso</Link></li>
                <li><a href="mailto:soporte@thetradingdock.com" className="hover:text-white transition">soporte@thetradingdock.com</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom strip */}
          <div className="pt-7 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
            <div>© 2026 <span className="text-slate-300 font-bold">Pryma Wellness Group Inc.</span> · Delaware, USA · The Trading Dock® es una marca registrada.</div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1.5"><Shield className="w-3 h-3 text-violet-400" /> JWT · bcrypt</span>
              <span>·</span>
              <span>Pagos por Stripe</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function SocialBtn({ href, label, testid, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={testid}
      className="w-9 h-9 rounded-full bg-white/[0.05] hover:bg-violet-500/20 border border-white/10 hover:border-violet-400/40 text-slate-400 hover:text-violet-200 flex items-center justify-center transition-all"
    >
      {children}
    </a>
  );
}

function CaseCard({ avatar, avatarColor, name, role, stat, quote, highlight = [] }) {
  // Highlight key phrases in quote (visual emphasis)
  let parts = [quote];
  highlight.forEach(term => {
    parts = parts.flatMap(p => typeof p === "string" ? p.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "i")) : [p]);
  });
  return (
    <div className="relative bg-white/[0.04] border border-white/10 rounded-3xl p-6 sm:p-7 flex flex-col hover:border-violet-400/30 transition-all" data-testid="case-card">
      <Quote className="absolute top-5 right-5 w-7 h-7 text-violet-500/30" />
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${avatarColor} flex items-center justify-center text-white text-xl font-black ring-1 ring-white/10`}>
          {avatar}
        </div>
        <div>
          <div className="font-bold text-sm">{name}</div>
          <div className="text-[11px] text-slate-400">{role}</div>
        </div>
      </div>
      <div className="text-xs text-violet-300 font-bold uppercase tracking-[0.15em] mb-3">{stat}</div>
      <p className="text-sm text-slate-300 leading-relaxed flex-1">
        &ldquo;{parts.map((p, i) => highlight.some(h => h.toLowerCase() === p?.toLowerCase()) ? (
          <strong key={i} className="text-white font-bold bg-violet-500/15 px-1 rounded">{p}</strong>
        ) : (
          <span key={i}>{p}</span>
        ))}&rdquo;
      </p>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-violet-400/30 hover:bg-white/[0.05] transition-all">
      <div className="w-11 h-11 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center text-violet-300 mb-4">
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-bold text-base">{title}</div>
      <div className="mt-1.5 text-sm text-slate-400 leading-relaxed">{desc}</div>
    </div>
  );
}

function BillToggle({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-sm font-bold px-5 py-2.5 rounded-xl transition-all flex items-center ${
        active ? "bg-violet-500 text-white shadow-[0_6px_20px_-6px_rgba(139,92,246,0.6)]" : "text-slate-300 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function LifetimeCard({ onClick, loading }) {
  return (
    <div className="relative bg-gradient-to-br from-amber-500/10 via-violet-500/10 to-fuchsia-500/10 border border-amber-400/30 rounded-3xl p-8 sm:p-10 overflow-hidden" data-testid="plan-lifetime">
      {/* Glow accent */}
      <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-amber-500/15 blur-[100px] pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-fuchsia-500/15 blur-[100px] pointer-events-none" />

      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <div className="bg-amber-500/20 border border-amber-400/40 rounded-full px-3 py-1 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-amber-200">
            <Zap className="w-3 h-3" /> Oferta de lanzamiento
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Crown className="w-8 h-8 text-amber-300" />
          <div className="text-2xl sm:text-3xl font-black tracking-tight">Acceso de por vida</div>
        </div>
        <p className="mt-2 text-sm text-slate-300">Un solo pago. Sin renovaciones. Sin cobros futuros.</p>

        <div className="mt-7 flex items-baseline gap-3">
          <span className="text-6xl sm:text-7xl font-black tabular-nums brand-mark">$100</span>
          <div>
            <div className="text-xs text-slate-500 line-through tabular-nums">$250 USD</div>
            <div className="text-xs text-amber-300 font-bold">Ahorras $150 (-60%)</div>
          </div>
        </div>

        <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
          {[
            "Todo lo del plan Pro incluido",
            "Acceso ilimitado a perpetuidad",
            "Nuevas funciones futuras gratis",
            "Sin renovaciones automáticas",
            "Soporte prioritario por vida",
            "Transferible a tu cuenta familiar (1)",
          ].map((f, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-slate-200">
              <Check className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <button
          onClick={onClick}
          disabled={loading}
          data-testid="plan-lifetime-cta"
          className="mt-8 w-full bg-gradient-to-r from-amber-400 via-violet-500 to-fuchsia-500 hover:opacity-90 text-white font-black py-4 rounded-2xl text-base shadow-[0_15px_60px_-15px_rgba(245,158,11,0.6)] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Activar mi acceso de por vida <ArrowRight className="w-5 h-5" /></>}
        </button>
        <div className="mt-4 text-center text-[11px] text-slate-500">Pago único seguro por Stripe · Sin renovaciones, nunca más cobros</div>
      </div>
    </div>
  );
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden group"
      open={open}
      onToggle={(e) => setOpen(e.target.open)}
      data-testid="faq-item"
    >
      <summary className="px-5 py-4 text-sm font-bold cursor-pointer flex items-center justify-between hover:bg-white/[0.02] transition-colors list-none">
        <span>{q}</span>
        <ArrowRight className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-90" : ""}`} />
      </summary>
      <div className="px-5 pb-4 text-sm text-slate-400 leading-relaxed">{a}</div>
    </details>
  );
}
